export type FeatureUrgency = 'low' | 'medium' | 'high' | 'critical';
export type FeaturePriority = 'P0' | 'P1' | 'P2' | 'P3';
export type FeatureStatus = 'submitted' | 'triage' | 'planned' | 'in-progress' | 'done';

export interface FeatureRequestInput {
  title: string;
  affectedArea: string;
  currentProblem: string;
  requestedFeature: string;
  productivityImpact: string;
  successCriteria: string;
  urgency: FeatureUrgency;
}

export interface FeatureBacklogItem extends FeatureRequestInput {
  id: string;
  createdAt: string;
  source: 'seeded-roadmap' | 'user-request';
  currentPage: string;
  priority: FeaturePriority;
  status: FeatureStatus;
}

const STORAGE_KEY = 'electroplm-feature-backlog-v1';
const priorityWeight: Record<FeaturePriority, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };
const statusWeight: Record<FeatureStatus, number> = { 'in-progress': 0, planned: 1, triage: 2, submitted: 3, done: 4 };

export const seededBacklog: FeatureBacklogItem[] = [
  {
    id: 'seed-role-aware-assistant',
    title: 'Role-aware ontology assistant guardrails',
    affectedArea: 'admin',
    currentProblem: 'The assistant can reason over products, changes, and release context, but it does not yet enforce role-sensitive boundaries for approval, admin, and governance actions.',
    requestedFeature: 'Add role-aware prompt and tool gating so assistant responses and suggested actions respect engineering, sourcing, admin, and approver responsibilities.',
    productivityImpact: 'Reduces unsafe guidance, shortens approval handoff confusion, and makes assistant output usable in real gated workflows.',
    successCriteria: 'Assistant explains role restrictions, avoids suggesting unauthorized actions, and tailors next steps based on user role.',
    urgency: 'critical',
    priority: 'P0',
    status: 'planned',
    source: 'seeded-roadmap',
    currentPage: '/admin',
    createdAt: '2026-04-09T00:00:00.000Z',
  },
  {
    id: 'seed-citations-evidence',
    title: 'Citations to PLM objects and GraphWiki pages',
    affectedArea: 'chatbot',
    currentProblem: 'The assistant can synthesize answers from tools, but users still need clearer traceability to the exact product, BOM, document, and ontology sources used.',
    requestedFeature: 'Add explicit citations or linked evidence blocks for operational records and GraphWiki pages in assistant answers.',
    productivityImpact: 'Improves trust, speeds validation, and reduces manual double-checking across modules.',
    successCriteria: 'Answers reference the source entities and wiki pages used, and users can quickly inspect the supporting evidence.',
    urgency: 'high',
    priority: 'P1',
    status: 'planned',
    source: 'seeded-roadmap',
    currentPage: '/features/ai',
    createdAt: '2026-04-09T00:00:00.000Z',
  },
  {
    id: 'seed-evaluation-harness',
    title: 'Ontology query evaluation harness',
    affectedArea: 'chatbot',
    currentProblem: 'There is no regression suite proving that ontology, impact, where-used, and release-readiness questions route to the right tools and produce grounded answers.',
    requestedFeature: 'Add evaluation scenarios and automated checks for ontology queries, graph traversal, and external-search fallbacks.',
    productivityImpact: 'Prevents silent regressions and speeds iteration on the assistant without manual smoke testing.',
    successCriteria: 'A repeatable test set exists for ontology-only, operational, mixed, and external question classes.',
    urgency: 'high',
    priority: 'P1',
    status: 'triage',
    source: 'seeded-roadmap',
    currentPage: '/features/ai',
    createdAt: '2026-04-09T00:00:00.000Z',
  },
  {
    id: 'seed-release-readiness-copilot',
    title: 'Release readiness copilot',
    affectedArea: 'cross-module',
    currentProblem: 'Release readiness is still distributed across BOM, document, change, and inventory views, forcing users to assemble the answer manually.',
    requestedFeature: 'Add a release readiness capability that checks released BOMs, supporting documents, open changes, and blocking supply conditions in one flow.',
    productivityImpact: 'Reduces handoffs between engineering, change control, and operations before release.',
    successCriteria: 'Users can ask whether a product or BOM revision is ready for release and get a grounded checklist with blockers.',
    urgency: 'medium',
    priority: 'P2',
    status: 'triage',
    source: 'seeded-roadmap',
    currentPage: '/pdm/bom',
    createdAt: '2026-04-09T00:00:00.000Z',
  },
];

export function derivePriority(urgency: FeatureUrgency): FeaturePriority {
  switch (urgency) {
    case 'critical': return 'P0';
    case 'high': return 'P1';
    case 'medium': return 'P2';
    case 'low':
    default:
      return 'P3';
  }
}

export function sortBacklog(items: FeatureBacklogItem[]) {
  return [...items].sort((left, right) => {
    const priorityDelta = priorityWeight[left.priority] - priorityWeight[right.priority];
    if (priorityDelta !== 0) return priorityDelta;

    const statusDelta = statusWeight[left.status] - statusWeight[right.status];
    if (statusDelta !== 0) return statusDelta;

    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function loadFeatureBacklog() {
  if (!canUseStorage()) return sortBacklog(seededBacklog);

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const seeded = sortBacklog(seededBacklog);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    return sortBacklog(JSON.parse(raw) as FeatureBacklogItem[]);
  } catch {
    const seeded = sortBacklog(seededBacklog);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

export function persistFeatureBacklog(items: FeatureBacklogItem[]) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sortBacklog(items)));
}

export function createFeatureBacklogItem(input: FeatureRequestInput, currentPage: string): FeatureBacklogItem {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    source: 'user-request',
    currentPage,
    priority: derivePriority(input.urgency),
    status: 'submitted',
    ...input,
  };
}